import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, PencilSimple, Trash, Coins, HandCoins, Paperclip, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate, fmtBRL, maskDocument } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CreditHistoryDialog } from "../components/CreditHistoryDialog";
import { ClientAttachmentsDialog } from "../components/ClientAttachmentsDialog";
import { useAuth } from "../context/AuthContext";
import { useIsDesktop, useDebouncedValue } from "../hooks/useResponsiveList";
import { Pagination } from "../components/Pagination";
import { SensitiveArea } from "../components/SensitiveArea";

export default function Clients() {
  return (
    <SensitiveArea>
      <ClientsContent />
    </SensitiveArea>
  );
}

const EMPTY = { name: "", status: "Ativo", phone: "", email: "", document: "", vendedor_id: "", ie: "", ie_isento: false, address: "", address_number: "", neighborhood: "", cep: "", city: "", state: "" };
const input = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

function ClientsContent() {
  const { user } = useAuth();
  const isVendedor = user?.role === "vendedor";
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const isDesktop = useIsDesktop();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [balances, setBalances] = useState({});
  const [creditClient, setCreditClient] = useState(null);
  const [attachClient, setAttachClient] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [cepLoading, setCepLoading] = useState(false);

  const handleCep = async (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    setForm((f) => ({ ...f, cep: masked }));
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) {
        toast.error("CEP não encontrado — preencha o endereço manualmente");
      } else {
        setForm((f) => ({
          ...f, cep: masked,
          address: data.logradouro || f.address,
          neighborhood: data.bairro || f.neighborhood,
          city: data.localidade || f.city,
          state: (data.uf || f.state || "").toUpperCase(),
        }));
        toast.success("Endereço preenchido pelo CEP");
      }
    } catch {
      toast.error("Não foi possível consultar o CEP — preencha manualmente");
    }
    setCepLoading(false);
  };

  useEffect(() => {
    if (!isVendedor) api.get("/users/vendedores").then((r) => setVendedores(r.data)).catch(() => {});
  }, [isVendedor]);

  const load = useCallback(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const params = { page, page_size: 50 };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    api.get("/clients", { params, signal: controller.signal })
      .then((r) => { setClients(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => { if (abortRef.current === controller) setLoading(false); });
    api.get("/reports/client-balances")
      .then((r) => setBalances(Object.fromEntries(r.data.clients.map((c) => [c.client_id, c.balance]))))
      .catch(() => {});
  }, [debouncedSearch, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente cadastrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este cliente?")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente excluído");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };

  const searchRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("buscar") === "1") {
      searchRef.current?.focus();
      searchParams.delete("buscar");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openEdit = (c) => {
    setForm({ name: c.name, status: c.status, phone: c.phone || "", email: c.email || "", document: c.document || "", vendedor_id: c.vendedor_id || "",
      ie: c.ie || "", ie_isento: !!c.ie_isento,
      address: c.address || "", address_number: c.address_number || "", neighborhood: c.neighborhood || "", cep: c.cep || "", city: c.city || "", state: c.state || "" });
    setEditing(c.id);
    setOpen(true);
  };

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-1">Clientes</h1>
          <p className="text-sm text-zinc-500">{clients.length} cliente(s) cadastrado(s)</p>
        </div>
        <button data-testid="add-client-button" onClick={openNew}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] text-white text-sm font-medium px-5 py-3 min-h-[44px] rounded-lg transition-all">
          <Plus size={18} weight="bold" /> Novo Cliente
        </button>
      </div>

      <div data-testid="clients-filters" className="sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-30 bg-zinc-950/95 backdrop-blur -mx-4 px-4 py-3 mb-4 border-b border-zinc-800/60 lg:relative lg:top-auto lg:z-20 lg:backdrop-blur-none lg:mx-0 lg:px-0 lg:py-0 lg:bg-transparent lg:border-0">
        <input
          data-testid="client-search-input"
          ref={searchRef}
          className={`${input} max-w-sm py-3 text-base`}
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Cards mobile */}
      {!isDesktop && (
      <div className="space-y-3" data-testid="clients-cards">
        {clients.length === 0 && <p className="text-center text-zinc-500 py-8">Nenhum cliente encontrado.</p>}
        {clients.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.code}`} className="border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-zinc-100 font-medium truncate">{c.name}</p>
                <p className="text-xs text-zinc-500 mt-0.5">#{c.code} · {c.document || "sem documento"}</p>
              </div>
              <StatusBadge status={c.status} />
            </div>
            <div className="text-xs text-zinc-400 space-y-1">
              {c.phone && <p>{c.phone}</p>}
              {c.email && <p className="truncate">{c.email}</p>}
              {c.vendedor_name && <p className="text-amber-400">Vendedor: {c.vendedor_name}</p>}
            </div>
            <div className="flex items-center justify-between border-t border-zinc-800/60 pt-2.5">
              <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Saldo devedor</span>
              {balances[c.id] ? (
                <span className="font-semibold text-amber-400">{fmtBRL(balances[c.id])}</span>
              ) : (
                <span className="text-zinc-500 text-sm">—</span>
              )}
            </div>
            <div className="flex gap-2">
              {user?.role === "admin" && (
                <button data-testid={`m-attachments-client-${c.code}`} onClick={() => setAttachClient({ id: c.id, name: c.name })} title="Anexos do cliente" aria-label="Anexos do cliente"
                  className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 active:bg-zinc-800 transition-colors"><Paperclip size={19} /></button>
              )}
              {!isVendedor && (
                <>
                  <button data-testid={`m-add-credit-client-${c.code}`} onClick={() => setCreditClient({ id: c.id, name: c.name, openAdd: true })} title="Adicionar crédito" aria-label="Adicionar crédito"
                    className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-emerald-400 active:bg-zinc-800 transition-colors"><HandCoins size={19} /></button>
                  <button data-testid={`m-credits-client-${c.code}`} onClick={() => setCreditClient({ id: c.id, name: c.name })} title="Extrato de créditos" aria-label="Extrato de créditos"
                    className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-emerald-400 active:bg-zinc-800 transition-colors"><Coins size={19} /></button>
                </>
              )}
              <button data-testid={`m-edit-client-${c.code}`} onClick={() => openEdit(c)} title="Editar cliente" aria-label="Editar cliente"
                className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-indigo-400 active:bg-zinc-800 transition-colors"><PencilSimple size={19} /></button>
              {!isVendedor && (
                <button data-testid={`m-delete-client-${c.code}`} onClick={() => remove(c.id)} title="Excluir cliente" aria-label="Excluir cliente"
                  className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-rose-400 active:bg-zinc-800 transition-colors"><Trash size={19} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {isDesktop && (
      <div className="border border-zinc-800 rounded-lg overflow-x-auto" data-testid="clients-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.1em] text-zinc-500 border-b border-zinc-800">
              <th className="px-5 py-3 font-medium sticky-col-l">ID</th>
              <th className="px-5 py-3 font-medium">Nome Completo</th>
              <th className="px-5 py-3 font-medium">CPF / CNPJ</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Telefone</th>
              <th className="px-5 py-3 font-medium">E-mail</th>
              <th className="px-5 py-3 font-medium">Vendedor</th>
              <th className="px-5 py-3 font-medium">Cadastro</th>
              <th className="px-5 py-3 font-medium">Saldo Devedor</th>
              <th className="px-5 py-3 font-medium text-right sticky-col-r">Ações</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr><td colSpan={10} className="px-5 py-8 text-center text-zinc-500">Nenhum cliente encontrado.</td></tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.code}`} className="border-b border-zinc-800/60 hover:bg-zinc-900 transition-colors">
                <td className="px-5 py-3 text-zinc-500 sticky-col-l">#{c.code}</td>
                <td className="px-5 py-3 text-zinc-200 font-medium">{c.name}</td>
                <td className="px-5 py-3 text-zinc-400" data-testid={`client-document-${c.code}`}>{c.document || "—"}</td>
                <td className="px-5 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-5 py-3 text-zinc-400">{c.phone || "—"}</td>
                <td className="px-5 py-3 text-zinc-400">{c.email || "—"}</td>
                <td className="px-5 py-3 text-zinc-400" data-testid={`client-vendedor-${c.code}`}>
                  {c.vendedor_name ? <span className="text-xs text-amber-400">{c.vendedor_name}</span> : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-5 py-3 text-zinc-400">{fmtDate(c.created_at)}</td>
                <td className="px-5 py-3" data-testid={`client-balance-cell-${c.code}`}>
                  {balances[c.id] ? (
                    <span className="font-medium text-amber-400">{fmtBRL(balances[c.id])}</span>
                  ) : (
                    <span className="text-zinc-500">—</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap sticky-col-r">
                  {user?.role === "admin" && (
                    <button data-testid={`attachments-client-${c.code}`} onClick={() => setAttachClient({ id: c.id, name: c.name })}
                      title="Anexos do cliente" className="text-zinc-400 hover:text-sky-400 p-1 transition-colors"><Paperclip size={16} /></button>
                  )}
                  {!isVendedor && (
                    <>
                      <button data-testid={`add-credit-client-${c.code}`} onClick={() => setCreditClient({ id: c.id, name: c.name, openAdd: true })}
                        title="Adicionar crédito manual" className="text-zinc-400 hover:text-emerald-400 p-1 ml-1 transition-colors"><HandCoins size={16} /></button>
                      <button data-testid={`credits-client-${c.code}`} onClick={() => setCreditClient({ id: c.id, name: c.name })}
                        title="Extrato de créditos" className="text-zinc-400 hover:text-emerald-400 p-1 ml-1 transition-colors"><Coins size={16} /></button>
                    </>
                  )}
                  <button data-testid={`edit-client-${c.code}`} onClick={() => openEdit(c)} title="Editar cliente" className="text-zinc-400 hover:text-indigo-400 p-1 ml-1 transition-colors"><PencilSimple size={16} /></button>
                  {!isVendedor && (
                    <button data-testid={`delete-client-${c.code}`} onClick={() => remove(c.id)} className="text-zinc-400 hover:text-rose-400 p-1 ml-1 transition-colors"><Trash size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <Pagination page={page} pageSize={50} total={total} loading={loading} onChange={setPage} testId="clients-pagination" label="cliente(s)" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Nome Completo</label>
              <input data-testid="client-name-input" className={input} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">CPF / CNPJ</label>
              <input data-testid="client-document-input" className={input} value={form.document}
                onChange={(e) => setForm({ ...form, document: maskDocument(e.target.value) })} placeholder="000.000.000-00 ou 00.000.000/0000-00" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Status</label>
              <select data-testid="client-status-select" className={input} value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option>Ativo</option>
                <option>Inativo</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Telefone</label>
              <input data-testid="client-phone-input" className={input} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-9999" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">E-mail</label>
              <input data-testid="client-email-input" type="email" className={input} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.com" />
            </div>
            <div className="border-t border-zinc-800 pt-3">
              <p className="text-xs uppercase tracking-[0.15em] text-indigo-400 mb-3">Dados fiscais e endereço (usados na NF-e)</p>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Inscrição Estadual</label>
                    <input data-testid="client-ie-input" className={`${input} disabled:opacity-60`} disabled={form.ie_isento}
                      value={form.ie_isento ? "ISENTO" : form.ie}
                      onChange={(e) => setForm({ ...form, ie: e.target.value })} placeholder="Somente números" />
                  </div>
                  <div className="flex items-end pb-2.5">
                    <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
                      <input type="checkbox" data-testid="client-ie-isento-checkbox" checked={form.ie_isento}
                        onChange={(e) => setForm({ ...form, ie_isento: e.target.checked, ie: e.target.checked ? "" : form.ie })}
                        className="accent-indigo-500 w-4 h-4" />
                      Isento
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Endereço (rua)</label>
                    <input data-testid="client-address-input" className={input} value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rua, avenida..." />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Número</label>
                    <input data-testid="client-address-number-input" className={input} value={form.address_number}
                      onChange={(e) => setForm({ ...form, address_number: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Bairro</label>
                    <input data-testid="client-neighborhood-input" className={input} value={form.neighborhood}
                      onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">CEP</label>
                    <div className="relative">
                      <input data-testid="client-cep-input" className={input} value={form.cep}
                        onChange={(e) => handleCep(e.target.value)} placeholder="00000-000" inputMode="numeric" maxLength={9} />
                      {cepLoading && (
                        <CircleNotch size={16} className="animate-spin text-indigo-400 absolute right-3 top-1/2 -translate-y-1/2" data-testid="cep-loading-spinner" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Município</label>
                    <input data-testid="client-city-input" className={input} value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">UF</label>
                    <input data-testid="client-state-input" className={input} value={form.state} maxLength={2}
                      onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} placeholder="SP" />
                  </div>
                </div>
              </div>
            </div>
            {!isVendedor && (
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Vendedor Responsável</label>
                <select data-testid="client-vendedor-select" className={input} value={form.vendedor_id}
                  onChange={(e) => setForm({ ...form, vendedor_id: e.target.value })}>
                  <option value="">Sem vendedor (carteira geral)</option>
                  {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <p className="text-xs text-zinc-500 mt-1">O vendedor só enxerga os clientes atribuídos a ele.</p>
              </div>
            )}
            <button data-testid="save-client-button"
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2.5 rounded transition-colors">
              Salvar
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <CreditHistoryDialog client={creditClient} onClose={() => setCreditClient(null)} onChanged={load} />
      <ClientAttachmentsDialog client={attachClient} onClose={() => setAttachClient(null)} />
    </div>
  );
}
